import React, { useState, useEffect, useContext, Fragment } from "react";
import { DownloadIcon, UploadIcon } from "@heroicons/react/outline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/solid";
import routes from "../../../config/routes-config";
import {
  fetchAdminStudentsData,
  deleteStudentByGrNo,
  downloadStudentsDataCsv,
  uploadStudentsDataCsv,
} from "./api-handlers";
import SearchBar from "../../../components/SearchBar";
import StudentTable from "./StudentTable";
import Pagination from "../../../components/pagination/Pagination";
import { LoginContext } from "../../../contexts/LoginContext.jsx";

const AdminStudentAccess = () => {
  const [studentsData, setStudentsData] = useState([]);

  const [studentsFilteredData, setStudentsFilteredData] = useState([]);

  const [departments, setDepartment] = useState([
    { departmentType: "All Branches", isSelected: true },
    { departmentType: "Chemical", isSelected: false },
    { departmentType: "Computer Science", isSelected: false },
    { departmentType: "Information Technolgy", isSelected: false },
    { departmentType: "Instrumentation", isSelected: false },
    { departmentType: "Electronics and Telecommunication", isSelected: false },
    { departmentType: "Mechanical", isSelected: false },
    { departmentType: "Production", isSelected: false },
  ]);

  const { userAuthStatus, user } = useContext(LoginContext);

  const [isFileSelected, setIsFileSelected] = useState("");

  // It is done for the initial request to fetch and save data
  const setData = (data) => {
    setStudentsData(data);
    setStudentsFilteredData(data);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);

  const indexOfLastStudent = currentPage * pageSize;
  const indexOfFirstStudent = indexOfLastStudent - pageSize;
  const currentStudents = studentsFilteredData.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  useEffect(() => {
    if (userAuthStatus && user !== null) {
      fetchAdminStudentsData(
        routes.admin.studentAccess.studentsAccessUrl,
        user.token,
        setData
      );
    }
  }, [userAuthStatus, user]);

  useEffect(() => {
    setTotalPages(Math.ceil(studentsFilteredData.length / pageSize));
  }, [pageSize, studentsFilteredData]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const prevPage = () => {
    setCurrentPage((previousState) => {
      if (previousState > 1 && previousState <= totalPages) {
        return previousState - 1;
      }

      return previousState;
    });
  };

  // Change page according to user click
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    setCurrentPage((previousState) => {
      if (previousState >= 1 && previousState < totalPages) {
        return previousState + 1;
      }

      return previousState;
    });
  };

  const selectDepartment = (departmentObj) => {
    const newDepartment = departments.map((department) => {
      if (department.departmentType === departmentObj.departmentType) {
        department.isSelected = true;
      } else {
        department.isSelected = false;
      }

      return department;
    });

    setDepartment(newDepartment);
  };

  const handleSearchingStudentsByNameOrGrNo = (query) => {
    try {
      const filteredData = studentsData.filter((student) => {
        return (
          query === "" ||
          student.name.toLowerCase().includes(query.trim().toLowerCase()) ||
          student.username.toLowerCase().includes(query.trim().toLowerCase())
        );
      });

      setStudentsFilteredData(filteredData);

      if (query.trim() !== "") {
        setCurrentPage(1);
      }
    } catch (err) {
      // console.log(err);
    }
  };

  const handleSortingStudentsByDept = (departmentObj) => {
    selectDepartment(departmentObj);

    try {
      const filteredData = studentsData.filter(
        (student) =>
          departmentObj.departmentType === "All Branches" ||
          student.department
            .toLowerCase()
            .includes(departmentObj.departmentType.toLowerCase())
      );

      setStudentsFilteredData(filteredData);
      setCurrentPage(1);
    } catch (err) {
      // console.log(err);
    }
  };

  const deleteStudentFromList = (username) => {
    setStudentsData(studentsData.filter((user) => user.username !== username));

    setStudentsFilteredData(
      studentsFilteredData.filter((user) => user.username !== username)
    );
  };

  const deleteStudent = (event, studentGRNo, setDeleteModalOpened) => {
    event.preventDefault();

    setDeleteModalOpened(false);

    if (userAuthStatus && user !== null) {
      deleteStudentByGrNo(
        `${routes.admin.studentAccess.deleteAStudentUrl}?username=${studentGRNo}`,
        user.token,
        studentGRNo,
        deleteStudentFromList,
        toast
      );
    }
  };

  const handleDownloadStudentsDataCsv = (event, filename) => {
    event.preventDefault();

    if (userAuthStatus && user !== null) {
      downloadStudentsDataCsv(
        routes.admin.studentAccess.downloadStudentsDataCsvUrl,
        user.token,
        filename,
        toast
      );
    }
  };

  // return true if file extension belongs to .xlsx, .xls, .csv
  const checkExtension = (file) => {
    const EXTENSIONS = ["xlsx", "xls", "csv"];

    const parts = file.name.split(".");
    const extension = parts[parts.length - 1];
    return EXTENSIONS.includes(extension);
  };

  const importFile = (event) => {
    const file = event.target.files[0];
    setIsFileSelected(file);
  };

  const handleUploadStudentsDataCsv = () => {
    if (isFileSelected === "") {
      toast.error("Please select a file to upload", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });

      return;
    }

    if (!checkExtension(isFileSelected)) {
      toast.error("Invalid file input, either select Excel or CSV file", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });

      return;
    }

    const formData = new FormData();
    formData.append("file", isFileSelected);

    if (userAuthStatus && user !== null) {
      uploadStudentsDataCsv(
        routes.admin.studentAccess.uploadStudentsDataCsvUrl,
        user.token,
        formData,
        toast
      );
    }
  };

  return (
    <>
      <div className="w-full flex flex-col">
        <div className="w-[74%] self-center mt-16">
          <SearchBar
            placeholder="Search by Name or GR/PR No"
            callbackSearch={handleSearchingStudentsByNameOrGrNo}
          />
        </div>

        <Menu as="div" className="relative inline-block text-right mt-4 mb-12">
          <div className="flex flex-row justify-end px-[13.5%]">
            <Menu.Button className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
              Sort By Department
              <ChevronDownIcon
                className="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="origin-top-right absolute right-[13%] mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
              {departments.map((department, index) => (
                <Menu.Item key={index}>
                  <div className="flex flex-row justify-between bg-gray-100 hover:bg-slate-200">
                    <button
                      onClick={() => handleSortingStudentsByDept(department)}
                      className="inline-block text-left px-4 py-2 text-lg w-[80%]"
                    >
                      {department.departmentType}
                    </button>
                    <CheckIcon
                      className={`${
                        department.isSelected ? "visible" : "invisible"
                      }  w-8 mr-[5%] fill-green-700`}
                    />
                  </div>
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>

        <div className="flex flex-col justify-center items-center">
          <StudentTable
            students={currentStudents}
            deleteStudent={deleteStudent}
          />
          <div className="mt-8 px-[12.5%]">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              prev={prevPage}
              paginate={paginate}
              next={nextPage}
            />
          </div>
        </div>

        <div className="flex flex-row justify-center self-center mt-10 mb-10 space-x-4 w-[75%]">
          <button
            className="inline-flex items-center min-w-fit rounded-lg bg-[#B4B0BB] px-6 py-2 text-lg font-semibold"
            type="button"
            onClick={(event) =>
              handleDownloadStudentsDataCsv(event, "students.csv")
            }
          >
            Download CSV
            <DownloadIcon className="w-6 ml-3" />
          </button>
          <input
            className="file:bg-[#B4B0BB] file:rounded-lg file:px-6 file:py-2 file:mr-8 outline-black-100 rounded-lg text-lg font-semibold"
            type="file"
            onChange={importFile}
          />
          <button
            className="inline-flex items-center min-w-fit rounded-lg bg-[#B4B0BB] md:px-6 md:py-2 text-lg font-semibold"
            type="button"
            onClick={handleUploadStudentsDataCsv}
          >
            Upload CSV
            <UploadIcon className="w-6 ml-3" />
          </button>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default AdminStudentAccess;
